import { zodResolver } from '@hookform/resolvers/zod';
import { panelSettingsSchema, type PanelSettings } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2, Settings as SettingsIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { BackupSettingsCard } from '@/components/backup-settings';
import { IntegrationSettingsCard } from '@/components/integration-settings';
import { FormField } from '@/components/form-field';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { apiRequest } from '@/lib/api';
import { applyServerErrors } from '@/lib/forms';
import { sessionQuery } from '@/lib/session';

export const Route = createFileRoute('/_app/admin/settings')({ component: SettingsPage });

const settingsQuery = {
  queryKey: ['admin', 'settings'],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest('/api/application/settings', { schema: panelSettingsSchema, signal }),
};

function SettingsPage() {
  const query = useQuery(settingsQuery);
  return (
    <>
      <PageHeader title="Settings" icon={SettingsIcon} description="Panel-wide configuration." />
      <div className="grid gap-6">
        <QueryState query={query}>{(data) => <SettingsForm initial={data} />}</QueryState>
        <BackupSettingsCard />
        <IntegrationSettingsCard />
      </div>
    </>
  );
}

function SettingsForm({ initial }: { initial: PanelSettings }) {
  const queryClient = useQueryClient();
  const form = useForm<PanelSettings>({
    resolver: zodResolver(panelSettingsSchema),
    values: initial,
  });
  const save = useMutation({
    mutationFn: (body: PanelSettings) =>
      apiRequest('/api/application/settings', {
        method: 'PATCH',
        body,
        schema: panelSettingsSchema,
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(settingsQuery.queryKey, data);
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      toast.success('Settings saved.');
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });

  const toggles: {
    name: 'registrationEnabled' | 'requireAdminTwoFactor';
    label: string;
    description: string;
  }[] = [
    {
      name: 'registrationEnabled',
      label: 'Allow registration',
      description: 'Anyone who can reach the panel can create an account.',
    },
    {
      name: 'requireAdminTwoFactor',
      label: 'Require 2FA for administrators',
      description:
        'Admins without two-factor authentication are blocked from the admin area until they enable it.',
    },
  ];

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>General</CardTitle>
        <CardDescription>The panel’s name, sign-up and administrator security.</CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <CardContent className="grid gap-6">
          <FormField
            label="Panel name"
            error={form.formState.errors.name?.message}
            description="Shown in the header, page titles and emails."
          >
            <Input {...form.register('name')} className="max-w-sm" />
          </FormField>
          {toggles.map((t) => (
            <Controller
              key={t.name}
              control={form.control}
              name={t.name}
              render={({ field }) => (
                <div className="flex items-start justify-between gap-6 border-t pt-5">
                  <div className="grid gap-1">
                    <Label htmlFor={t.name}>{t.label}</Label>
                    <p className="text-muted-foreground text-xs">{t.description}</p>
                  </div>
                  <Switch id={t.name} checked={field.value} onCheckedChange={field.onChange} />
                </div>
              )}
            />
          ))}
        </CardContent>
        <CardFooter className="mt-6 justify-end border-t">
          <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
            {save.isPending && <Loader2 className="animate-spin" />} Save settings
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
